import {useParams, Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useConsultantDetailQuery} from "../../../api/consultants/api";
import HeaderImage from "../../../components/HeaderImage";
import Heading from "../../../components/Heading";
import Loader from "../../../components/Loader";

const ConsultantDetail = () => {
  const {t} = useTranslation();
  const {id} = useParams();
  const consultantId = parseInt(id || '0');
  const {data: consultant, isFetching: isLoading} = useConsultantDetailQuery(consultantId);

  const placeholderImage = '/logo.jpg';

  if (isLoading) return <Loader />;

  if (!consultant) {
    return (
      <div className="content-bg">
        <HeaderImage imageUrl="/pages/01_o-nas.jpg" />
        <div className="container mx-auto">
          <div className="bg-white p-8 md:py-16 text-center">
            <p className="text-gray-500">{t('consultantFinder.notFound')}</p>
            <Link to="/pages/help" className="text-blue-500 hover:text-blue-700 mt-4 inline-block">
              {t('consultantFinder.backToMap')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-bg">
      <HeaderImage imageUrl="/pages/01_o-nas.jpg" />
      <div className="container mx-auto">
        <div className="bg-white p-8 md:py-16">
          <Link to="/pages/help" className="text-blue-500 hover:text-blue-700 mb-6 inline-block">
            &larr; {t('consultantFinder.backToMap')}
          </Link>

          <Heading>{consultant.name}</Heading>

          <div className="grid md:grid-cols-3 gap-8 mt-8">
            {/* Profile image */}
            <div className="md:col-span-1">
              <img
                src={consultant.profile_image || placeholderImage}
                alt={consultant.name}
                className="w-full max-w-xs rounded-lg shadow-md"
              />
            </div>

            {/* Details */}
            <div className="md:col-span-2">
              {/* Company name + logo + address */}
              <div className="mb-6">
                <div className="flex items-start gap-4">
                  {consultant.registration?.logo && (
                    <img
                      src={consultant.registration.logo}
                      alt={consultant.registration.name || ''}
                      className="w-16 h-16 object-contain flex-shrink-0 rounded"
                    />
                  )}
                  <div>
                    {consultant.registration?.name && (
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {consultant.registration.name}
                      </h3>
                    )}
                    {consultant.registration?.number && (
                      <p className="text-sm text-gray-500 mb-2">
                        {t('consultantFinder.registration')}: #{consultant.registration.number}
                      </p>
                    )}
                    <p className="text-gray-600">
                      {consultant.street && <>{consultant.street}<br /></>}
                      {consultant.zip} {consultant.city}
                    </p>
                  </div>
                </div>
              </div>

              {/* Website */}
              {consultant.registration?.website && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-2">{t('consultantFinder.website')}</h4>
                  <a
                    href={consultant.registration.website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-500 hover:text-blue-700 break-all"
                  >
                    {consultant.registration.website}
                  </a>
                </div>
              )}

              {/* Description */}
              {consultant.registration?.description && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-2">{t('consultantFinder.description')}</h4>
                  <p className="text-gray-600 whitespace-pre-line">{consultant.registration.description}</p>
                </div>
              )}

              {/* Contact */}
              {(consultant.email || consultant.phone) && (
                <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {consultant.email && (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">{t('consultantFinder.email')}</h4>
                      <a href={`mailto:${consultant.email}`} className="text-blue-500 hover:text-blue-700 break-all">
                        {consultant.email}
                      </a>
                    </div>
                  )}
                  {consultant.phone && (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">{t('consultantFinder.phone')}</h4>
                      <a href={`tel:${consultant.phone}`} className="text-blue-500 hover:text-blue-700">
                        {consultant.phone}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Regions */}
              {consultant.regions.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-2">{t('consultantFinder.regions')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {consultant.regions.map(region => (
                      <span key={region.id} className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                        {region.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Sectors */}
              {consultant.sectors.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-2">{t('consultantFinder.sectors')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {consultant.sectors.map(sector => (
                      <span key={sector.id} className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">
                        {sector.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantDetail;
